<template>
  <v-container fluid>

    <!-- Language selector -->
    <div class="pt-3 pb-1">
      <div class="field-label text-left pl-4">
        {{ $t('dash.shadowDictionary.selectLanguage') }}
      </div>
      <v-select
        v-model="selectedLanguage"
        :items="languageOptions"
        item-text="label"
        item-value="code"
        outlined
        dense
        hide-details
        @change="loadEntries"
      />
    </div>

    <!-- Search bar + scope chips (visible once entries are loaded) -->
    <div v-if="entries.length > 0" class="pb-3">
      <div class="field-label text-left pl-4">
        {{ $t('dash.shadowDictionary.search') }}
      </div>
      <div class="d-flex align-center">
        <v-text-field
          v-model="searchQuery"
          append-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          class="flex-grow-1"
        />
        <!-- Search scope chips -->
        <div class="d-flex align-center ml-2 flex-shrink-0">
          <v-chip
            v-for="opt in scopeOptions"
            :key="opt.value"
            x-small
            :color="searchScope === opt.value ? '#00A9B3' : ''"
            :outlined="searchScope !== opt.value"
            :dark="searchScope === opt.value"
            class="scope-chip mr-1"
            @click="searchScope = opt.value"
          >
            {{ opt.label }}
          </v-chip>
        </div>
      </div>
      <!-- Manual-only filter: shows entries that were manually overridden -->
      <div class="mt-2 d-flex align-center">
        <v-chip
          x-small
          :color="showManualOnly ? 'orange darken-1' : ''"
          :outlined="!showManualOnly"
          :dark="showManualOnly"
          class="manual-chip"
          @click="showManualOnly = !showManualOnly"
        >
          {{ $t('dash.shadowDictionary.filterManual') }}
        </v-chip>
      </div>
    </div>

    <!-- Loading spinner -->
    <div v-if="loading" class="d-flex justify-center pa-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <!-- Global error -->
    <v-alert
      v-if="globalError"
      type="error"
      class="ma-4"
      dense
      dismissible
      @input="globalError = null"
    >
      {{ globalError }}
    </v-alert>

    <!-- Empty state -->
    <div
      v-if="!loading && selectedLanguage && entries.length === 0 && !globalError"
      class="pa-4 text-center grey--text"
    >
      {{ $t('dash.shadowDictionary.noEntries') }}
    </div>

    <!-- Entry list -->
    <div v-for="entry in filteredEntries" :key="entry.id">

      <!-- Collapsed row -->
      <DetailListItem
        @click.native="toggleEntry(entry)"
        icon-right
        icon="arrow-down-pink.svg"
        height="auto"
      >
        <div class="entry-row">
          <span class="original-text">{{ entry.originalTitle }}</span>
          <span class="divider-text">&#8594;</span>
          <span :class="['translated-text', entry.editable ? 'text--warning' : '']">
            {{ entry.title }}
          </span>
          <!-- Auto = DeepL, Manual = admin override -->
          <v-chip
            x-small
            :color="entry.editable ? 'orange darken-1' : 'teal'"
            dark
            class="ml-2"
          >
            {{ entry.editable
              ? $t('dash.shadowDictionary.statusManual')
              : $t('dash.shadowDictionary.statusAuto')
            }}
          </v-chip>
        </div>
      </DetailListItem>

      <!-- Expanded section -->
      <div
        :id="'shadow-entry-' + entry.id + '-content'"
        class="content-closed entry-content pa-3"
      >

        <!-- Original Dutch source -->
        <div class="original-block mb-3 pa-3">
          <div class="caption grey--text mb-1 text-left">
            {{ $t('dash.shadowDictionary.original') }}
          </div>
          <div class="subtitle-2 font-weight-bold text-left">
            {{ entry.originalTitle }}
          </div>
          <div class="body-2 mt-1 text-left">
            {{ entry.originalDescription }}
          </div>
        </div>

        <!-- Translated title -->
        <v-text-field
          v-model="entry.title"
          outlined
          dense
          :label="$t('dash.shadowDictionary.translatedTitle')"
          class="mb-2"
        />

        <!-- Translated description -->
        <v-textarea
          v-model="entry.description"
          outlined
          rows="5"
          class="mb-2 description-textarea"
          :label="$t('dash.shadowDictionary.translatedDescription')"
          :ref="'textarea-' + entry.id"
          @input="resizeTextarea(entry)"
        />

        <!-- Feedback messages -->
        <v-alert v-if="entry.successMessage" type="success" dense class="mb-2">
          {{ entry.successMessage }}
        </v-alert>

        <v-alert v-if="entry.errorMessage" type="error" dense class="mb-2">
          {{ entry.errorMessage }}
        </v-alert>

        <!-- Action buttons -->
        <div class="d-flex align-center">
          <!-- PUT /api/shadow-dictionary/{id} -->
          <v-btn
            color="primary"
            small
            :loading="entry.saving"
            :disabled="!entry.title || !entry.description"
            @click="saveEntry(entry)"
          >
            {{ $t('dash.shadowDictionary.save') }}
          </v-btn>

          <!-- POST /api/shadow-dictionary/{id}/reset — only visible when manually overridden -->
          <v-btn
            v-if="entry.editable"
            text
            small
            color="warning"
            :loading="entry.resetting"
            class="ml-2"
            @click="resetEntry(entry)"
          >
            {{ $t('dash.shadowDictionary.reset') }}
          </v-btn>
        </div>

      </div>
    </div>

  </v-container>
</template>

<script>
import { neoparentShadowDictionary } from "@/api";
import { redirect } from "@/helpers";

export default {
  name: "neoparent-shadow-dictionary",

  data() {
    return {
      selectedLanguage: null,
      entries: [],
      loading: false,
      globalError: null,
      searchQuery: "",
      searchDebounced: "",
      // Search scope: 'title' | 'description' | 'both'
      searchScope: "both",
      // When true, only manually overridden entries are shown
      showManualOnly: false,
      availableLanguages: []
    };
  },

  watch: {
    searchQuery(val) {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.searchDebounced = val || "";
      }, 500);
    }
  },

  computed: {
    filteredEntries() {
      let list = this.showManualOnly ? this.entries.filter(e => e.editable) : this.entries;
      const q = this.searchDebounced.trim().toLowerCase();
      if (!q) return list;
      return list.filter(e => {
        const inTitle =
          (e.originalTitle || "").toLowerCase().includes(q) ||
          (e.title || "").toLowerCase().includes(q);
        const inDesc =
          (e.originalDescription || "").toLowerCase().includes(q) ||
          (e.description || "").toLowerCase().includes(q);
        if (this.searchScope === "title") return inTitle;
        if (this.searchScope === "description") return inDesc;
        return inTitle || inDesc;
      });
    },

    scopeOptions() {
      return [
        { value: "title",       label: this.$t("dash.shadowDictionary.scopeTitle") },
        { value: "description", label: this.$t("dash.shadowDictionary.scopeDesc") },
        { value: "both",        label: this.$t("dash.shadowDictionary.scopeBoth") }
      ];
    },

    languageOptions() {
      return this.availableLanguages.map(l => ({
        code: l.code,
        label: l.displayName || this.$t("general.languages." + l.code)
      }));
    }
  },

  async mounted() {
    if (this.$store.getters.user.role !== "a") {
      redirect("/dashboard");
      return;
    }

    await this.loadLanguageOptions();
    window.addEventListener("resize", this.resizeAllTextareas);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.resizeAllTextareas);
  },

  methods: {

    async loadLanguageOptions() {
      try {
        const res = await neoparentShadowDictionary.getLanguages();
        this.availableLanguages = res.data?.languages || [];
      } catch (err) {
        this.availableLanguages = [];
        this.globalError = err?.response?.data?.message || "Error loading languages.";
      }
    },

    toggleEntry(entry) {
      const el = document.getElementById(
        "shadow-entry-" + entry.id + "-content"
      );

      if (!el) return;

      const isOpening = el.classList.contains("content-closed");
      el.classList.toggle("content-closed");

      if (isOpening) {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.resizeTextarea(entry);
          });
        });
      }
    },

    resizeAllTextareas() {
      this.entries.forEach(e => this.resizeTextarea(e));
    },

    resizeTextarea(entry) {
      const wrapperRef = this.$refs["textarea-" + entry.id];
      const wrapper = Array.isArray(wrapperRef) ? wrapperRef[0] : wrapperRef;
      if (!wrapper) return;

      const root = wrapper.$el || wrapper;
      if (!root || typeof root.querySelector !== "function") return;

      const textarea = root.querySelector("textarea");
      if (!textarea) return;

      const slot = textarea.closest(".v-input__slot");

      const minLines = 5;
      const maxLines = 20;

      // Reset both heights so scrollHeight reflects true content
      textarea.style.height = "auto";
      if (slot) slot.style.height = "auto";

      const style = window.getComputedStyle(textarea);
      const lineHeight = parseFloat(style.lineHeight) || 24;
      const padding =
        parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);

      const lines = Math.ceil(
        (textarea.scrollHeight - padding) / lineHeight
      );

      const visible = Math.min(maxLines, Math.max(minLines, lines));
      const newHeight = visible * lineHeight + padding + "px";

      textarea.style.height = newHeight;
      textarea.style.overflowY = lines > maxLines ? "auto" : "hidden";

      // Vuetify's slot must match, otherwise it clips the textarea
      if (slot) slot.style.height = newHeight;
    },

    async loadEntries() {
      if (!this.selectedLanguage) return;

      this.loading = true;
      this.globalError = null;
      this.entries = [];
      this.searchQuery = "";
      this.searchDebounced = "";
      this.showManualOnly = false;

      try {
        const res =
          await neoparentShadowDictionary.getByLanguage(
            this.selectedLanguage
          );

        this.entries = (res.data.entries || []).map(e => ({
          ...e,
          saving: false,
          resetting: false,
          successMessage: null,
          errorMessage: null
        }));

      } catch (err) {
        this.globalError = err?.response?.data?.message || "Error loading entries.";
      } finally {
        this.loading = false;
      }
    },

    async saveEntry(entry) {
      if (!entry.title || !entry.description) return;

      entry.saving = true;
      entry.successMessage = null;
      entry.errorMessage = null;

      try {
        const res =
          await neoparentShadowDictionary.updateManual(
            entry.id,
            {
              title: entry.title.trim(),
              description: entry.description.trim()
            }
          );

        entry.editable = res.data.entry.editable;
        entry.successMessage =
          this.$t("dash.shadowDictionary.saveSuccess");

        setTimeout(() => {
          entry.successMessage = null;
        }, 3000);

      } finally {
        entry.saving = false;
      }
    },

    async resetEntry(entry) {
      if (!confirm(this.$t("dash.shadowDictionary.resetConfirm")))
        return;

      entry.resetting = true;

      try {
        const res =
          await neoparentShadowDictionary.resetToAuto(entry.id);

        entry.title = res.data.entry.title;
        entry.description = res.data.entry.description;
        entry.editable = res.data.entry.editable;

        this.$nextTick(() =>
          requestAnimationFrame(() =>
            this.resizeTextarea(entry)
          )
        );

      } finally {
        entry.resetting = false;
      }
    }
  }
};
</script>

<style scoped>
/* Allow the Vuetify slot wrapper to grow with the textarea */
.description-textarea ::v-deep .v-input__slot {
  height: auto !important;
  overflow: visible !important;
}

.description-textarea textarea {
  resize: none;
}

.field-label {
  font-size: 0.75rem;
  color: #888;
  margin-bottom: 4px;
  letter-spacing: 0.02em;
}

/* Inactive scope chips use the app's mint border */
.scope-chip.v-chip--outlined {
  border-color: #afdfd3 !important;
  color: #9e9e9e !important;
}

.scope-chip.v-chip--outlined:hover {
  background-color: #D9F0EA !important;
  color: #00A9B3 !important;
}

/* Manual filter chip: outlined orange when inactive */
.manual-chip.v-chip--outlined {
  border-color: #EF6C00 !important;
  color: #EF6C00 !important;
}

.manual-chip.v-chip--outlined:hover {
  background-color: #FFF3E0 !important;
}
</style>